import React, { useState, useEffect } from 'react';
import MultiSelectTicker from '../MultiSelectTicker';
import { getSortedTickerHistory, trackTickerAccess, formatLastAccessed } from '../../utils/tickerHistory';

function ResearchTickerInput({ onSubmit, processing, errors }) {
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [recentTickers, setRecentTickers] = useState([]);

  // Load recent ticker history on mount and listen for updates
  useEffect(() => {
    setRecentTickers(getSortedTickerHistory());

    const handleUpdate = () => {
      setRecentTickers(getSortedTickerHistory());
    };
    window.addEventListener('tickerHistoryUpdated', handleUpdate);
    return () => window.removeEventListener('tickerHistoryUpdated', handleUpdate);
  }, []);

  const handleTickerChange = (selectedOptions) => {
    setSelectedTickers(selectedOptions || []);
  };

  const handleQuickSelect = (ticker) => {
    const exists = selectedTickers.some(t => t.value === ticker);
    if (!exists) {
      setSelectedTickers(prev => [...prev, { label: ticker, value: ticker }]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (selectedTickers.length === 0) {
      alert('Please enter at least one ticker symbol');
      return;
    }

    const tickerList = selectedTickers.map(t => t.value.toUpperCase());

    // Track each ticker in history
    tickerList.forEach(ticker => trackTickerAccess(ticker, 'research'));

    onSubmit(tickerList);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-4">Analysis Input</h2>
        <p className="text-gray-400 mb-6">
          Enter ticker symbols to run fundamentals analysis.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Ticker Input */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Ticker Symbols *
          </label>
          <MultiSelectTicker
            value={selectedTickers}
            onChange={handleTickerChange}
            placeholder="Search and select tickers..."
          />
        </div>

        {/* Recent Searches Quick Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Recent Searches
          </label>
          {recentTickers.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {recentTickers.map(item => (
                <button
                  key={item.ticker}
                  type="button"
                  onClick={() => handleQuickSelect(item.ticker)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    selectedTickers.some(t => t.value === item.ticker)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                  disabled={processing}
                  title={`Last used: ${formatLastAccessed(item.lastAccessed)}`}
                >
                  {item.ticker}
                </button>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic">
              No recent searches yet. Your analyzed tickers will appear here.
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={processing || selectedTickers.length === 0}
            className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {processing ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Analyzing...
              </div>
            ) : (
              'Run Analysis'
            )}
          </button>
        </div>
      </form>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-900/20 border border-red-700 rounded-md p-4">
          <h3 className="text-red-400 font-medium mb-2">Analysis Errors:</h3>
          <ul className="text-red-300 text-sm space-y-1">
            {errors.map((error, index) => (
              <li key={index}>- {error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default ResearchTickerInput;
